/**
 * PortfolioActionLink — May 2026.
 *
 * Compact pill button used to scatter the old portfolio action-bar buttons
 * (News, Calendar, DRIP Calculator, Rebalancer, What If?, Compare) into
 * their context-relevant boxes around the Portfolio page.
 *
 * Mirrors the visual style of the original action bar (`flex items-center
 * gap-1.5 px-3 py-1.5 text-sm`), with two opt-in modifiers:
 *
 *   • `sizeBoost` (0-3) — bumps the label font + icon by N pixels above
 *     the 14px / 16px baseline. Used on the Portfolio page to dial in the
 *     exact sizes the user asked for per card (What If/Rebalancer/DRIP
 *     +1px, Compare +2px, etc.).
 *   • `bordered` — adds a neutral border + faint muted background, matching
 *     the bordered-pill style introduced on the Capital Tracker card's
 *     Calculator button so the action affordances visually unify across
 *     every card on the Portfolio page.
 *
 * `tone` colours the icon — defaults to muted for generic links; pass
 * a Tailwind colour class string to brand the icon (e.g. "text-blue-500").
 */
export default function PortfolioActionLink({
  icon: Icon,
  label,
  onClick,
  tone = 'text-muted-foreground',
  testId,
  title,
  active = false,
  sizeBoost = 0,
  bordered = false,
}) {
  const textPx = 14 + (Number(sizeBoost) || 0);
  const iconPx = 16 + (Number(sizeBoost) || 0);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold transition-all whitespace-nowrap ${
        active
          ? 'bg-primary text-primary-foreground shadow-sm'
          : bordered
            ? 'border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
      style={{ fontSize: `${textPx}px` }}
      data-testid={testId}
      title={title}
    >
      <Icon
        className={`shrink-0 ${active ? '' : tone}`}
        style={{ width: `${iconPx}px`, height: `${iconPx}px` }}
      />
      <span>{label}</span>
    </button>
  );
}
